<template>
  <div>
    <div class="heading">
      <div>
        <h1>健康总览</h1>
        <p>所有服务器和 agent 的当前状态与最近 7 天的运行情况</p>
      </div>
      <a-space>
        <span v-if="overview" class="muted">更新于 {{ updatedAt }}</span>
        <a-button :loading="loading" @click="load">刷新</a-button>
      </a-space>
    </div>

    <MaintenanceBanner :state="overview?.maintenance ?? null" @changed="onMaintenanceChanged" />

    <a-alert v-if="loadError" type="error" show-icon :message="loadError" class="block" />

    <template v-if="overview">
      <div class="cards">
        <button type="button" class="card ok" :class="{ active: filter === 'all' }" @click="filter = 'all'">
          <span>全部</span><b>{{ overview.summary.total }}</b>
        </button>
        <div class="card ok"><span>正常</span><b>{{ overview.summary.ok }}</b></div>
        <button type="button" class="card attention" :class="{ active: filter === 'problems' }" @click="filter = 'problems'">
          <span>需关注 / 异常</span><b>{{ overview.summary.attention }} / {{ overview.summary.critical }}</b>
        </button>
        <button type="button" class="card info" :class="{ active: filter === 'outdated' }" @click="filter = 'outdated'">
          <span>待升级（周四）</span><b>{{ overview.summary.outdated }}</b>
        </button>
      </div>

      <a-alert v-if="recordingNote" type="info" show-icon :message="recordingNote" class="block" />

      <div class="toolbar">
        <a-radio-group v-model:value="filter" button-style="solid" size="small">
          <a-radio-button value="all">全部</a-radio-button>
          <a-radio-button value="problems">需关注 / 异常</a-radio-button>
          <a-radio-button value="outdated">待升级</a-radio-button>
        </a-radio-group>
        <a-input-search v-model:value="query" allow-clear placeholder="搜索服务器、服务商、地区" style="width: 260px" />
      </div>

      <a-table
        :columns="columns"
        :data-source="rows"
        :pagination="false"
        size="small"
        row-key="id"
        :custom-row="(record: HealthNode) => ({ onClick: () => openDrawer(record), style: 'cursor:pointer' })"
      >
        <template #emptyText>没有符合条件的服务器</template>
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <a-tag :color="levelMeta(record.level).color" class="tag">{{ levelMeta(record.level).label }}</a-tag>
            <div class="sub">{{ record.status === 'online' ? formatAge(record.heartbeat_age_seconds) : '离线' }}</div>
          </template>
          <template v-else-if="column.key === 'name'">
            <div class="name">{{ record.name }}</div>
            <div class="sub">{{ [record.provider, record.region].filter(Boolean).join(' · ') || '—' }}</div>
          </template>
          <template v-else-if="column.key === 'reasons'">
            <div v-if="!record.reasons.length" class="sub">—</div>
            <div v-for="r in record.reasons.slice(0, 2)" :key="r.text" class="reason" :class="r.severity" :title="r.text">{{ r.text }}</div>
            <div v-if="record.reasons.length > 2" class="sub">还有 {{ record.reasons.length - 2 }} 项</div>
          </template>
          <template v-else-if="column.key === 'resources'">
            <template v-if="record.status === 'online'">
              <div class="mono">CPU {{ pct(record.cpu_percent) }}</div>
              <div class="mono">内存 {{ pct(record.mem_percent) }} · 磁盘 {{ pct(record.disk_percent) }}</div>
            </template>
            <span v-else class="sub">—</span>
          </template>
          <template v-else-if="column.key === 'proxies'">
            <div>{{ record.active_proxies }} 个 proxy<span v-if="record.missing_children" class="bad"> · 缺 {{ record.missing_children }}</span></div>
            <div class="sub">{{ record.live_connections }} 条连接</div>
          </template>
          <template v-else-if="column.key === 'version'">
            <div class="mono ellipsis" :title="record.agent_version">{{ record.agent_version || '—' }}</div>
            <a-tag v-if="isOutdated(record, overview.latest_agent_version)" color="blue" class="tag">待升级</a-tag>
          </template>
          <template v-else-if="column.key === 'week'">
            <div class="week-head">
              <b :class="'tone-' + uptimeTone(record.uptime_7d)">{{ formatUptime(record.uptime_7d) }}</b>
              <span class="sub">掉线 {{ record.offline_7d }} · 重连 {{ record.reconnects_7d }}</span>
            </div>
            <HealthStrip :cells="record.strip" :generated-at="overview.generated_at" />
          </template>
        </template>
      </a-table>
    </template>

    <a-spin v-else-if="loading" style="display:block;margin:60px auto" />

    <NodeHealthDrawer v-model:visible="drawerOpen" :node="selected" :generated-at="overview?.generated_at ?? ''" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { getHealthOverview } from '../api/health';
import HealthStrip from '../components/HealthStrip.vue';
import MaintenanceBanner from '../components/MaintenanceBanner.vue';
import NodeHealthDrawer from '../components/NodeHealthDrawer.vue';
import { filterHealthNodes, isOutdated, type HealthFilter } from '../components/healthFilter';
import { formatAge, formatUptime, levelMeta, uptimeTone } from '../components/healthPresentation';
import type { HealthNode, HealthOverview, MaintenanceState } from '../types';

const REFRESH_MS = 30_000;

const overview = ref<HealthOverview | null>(null);
const loading = ref(false);
const loadError = ref('');
const filter = ref<HealthFilter>('all');
const query = ref('');
const drawerOpen = ref(false);
const selectedId = ref<number | null>(null);
let timer: ReturnType<typeof setInterval> | undefined;

const rows = computed(() =>
  overview.value
    ? filterHealthNodes(overview.value.nodes, { filter: filter.value, query: query.value }, overview.value.latest_agent_version)
    : [],
);
// Looked up by id so the open drawer follows each refresh.
const selected = computed<HealthNode | null>(() => overview.value?.nodes.find((n) => n.id === selectedId.value) ?? null);
const updatedAt = computed(() => (overview.value ? new Date(overview.value.generated_at).toLocaleTimeString('zh-CN', { hour12: false }) : ''));

// History only exists from the moment recording started.
const recordingNote = computed(() => {
  const since = overview.value?.recording_since;
  if (!since) return '还没有历史记录：健康数据从现在开始累积，下方的 7 天色条会逐渐填满。';
  const days = (Date.now() - new Date(since).getTime()) / 86400_000;
  if (days >= 7) return '';
  return `历史记录从 ${new Date(since).toLocaleString('zh-CN', { hour12: false })} 开始，更早的时间没有数据（灰色）。`;
});

const columns = [
  { title: '状态', key: 'status', width: 92 },
  { title: '服务器', key: 'name', width: 170 },
  { title: '问题', key: 'reasons', width: 230 },
  { title: '资源', key: 'resources', width: 150 },
  { title: 'proxy', key: 'proxies', width: 110 },
  { title: 'agent 版本', key: 'version', width: 150 },
  { title: '最近 7 天', key: 'week', width: 380 },
];

const pct = (v: number | null) => (v == null ? '—' : `${Math.round(v)}%`);

const load = async () => {
  loading.value = true;
  try {
    overview.value = (await getHealthOverview()).data;
    loadError.value = '';
  } catch (e: any) {
    loadError.value = e?.response?.data?.error?.message || e?.message || '加载健康数据失败';
  } finally {
    loading.value = false;
  }
};

const onMaintenanceChanged = (state: MaintenanceState) => {
  if (overview.value) overview.value = { ...overview.value, maintenance: state };
};

const openDrawer = (record: HealthNode) => {
  selectedId.value = record.id;
  drawerOpen.value = true;
};

onMounted(() => {
  load();
  timer = setInterval(load, REFRESH_MS);
});
onBeforeUnmount(() => timer && clearInterval(timer));
</script>

<style scoped>
.heading { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 16px; }
.heading h1 { margin: 0; }
.heading p { margin: 4px 0 0; color: #8c8c8c; }
.muted, .sub { color: #9ca3af; font-size: 12px; }
.block { margin-bottom: 16px; }
.cards { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
.card { display: flex; flex-direction: column; gap: 4px; padding: 12px 16px; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; text-align: left; font: inherit; }
button.card { cursor: pointer; }
.card span { color: #6b7280; font-size: 12px; }
.card b { font-size: 22px; line-height: 1.2; }
.card.ok b { color: #389e0d; }
.card.attention b { color: #d46b08; }
.card.info b { color: #1677ff; }
.card.active { border-color: #1677ff; box-shadow: 0 0 0 1px #1677ff; }
.toolbar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.tag { margin-inline-end: 0; }
.name { font-weight: 500; }
.mono { font-family: ui-monospace, Menlo, monospace; font-size: 12px; }
.ellipsis { max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.reason { font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 220px; }
.reason.critical { color: #cf1322; }
.reason.attention { color: #d46b08; }
.reason.info { color: #6b7280; }
.bad { color: #cf1322; }
.week-head { display: flex; align-items: baseline; gap: 10px; margin-bottom: 4px; }
.tone-good { color: #389e0d; }
.tone-warn { color: #d48806; }
.tone-bad { color: #cf1322; }
.tone-none { color: #9ca3af; }
@media (max-width: 900px) { .cards { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
</style>
